import { useQuery } from '@apollo/client';

import { ExperimentalFeaturesDocument } from '@/__generated__/graphql';

/**
 * SYN-205 — the client-side experimental-feature gating seam.
 *
 * `useExperimentalFeature('some-feature')` answers "is this Labs feature on for
 * the current workspace?" from the `experimentalFeatures` query (cache-first —
 * one fetch serves every consumer plus the settings page). Fail-closed by
 * design: while loading, on error, or for an id the server doesn't list, the
 * answer is `false` — an experimental surface must never flash on before the
 * flag state is known.
 *
 * The server value is already the EFFECTIVE state (per-tenant override or the
 * registry default), so no default-resolution happens here.
 */
export function useExperimentalFeature(featureId: string): boolean {
  const { data } = useQuery(ExperimentalFeaturesDocument, { fetchPolicy: 'cache-first' });
  const feature = data?.experimentalFeatures.find((f) => f.id === featureId);
  return feature?.enabled ?? false;
}

/** The loading-aware state of an experimental flag. */
export interface ExperimentalFeatureGate {
  /** EFFECTIVE enabled state (fail-closed false while loading / on error / unknown id). */
  readonly enabled: boolean;
  /** True until the first flag state is known — lets a ROUTE render a fallback instead of flashing 404. */
  readonly loading: boolean;
}

/**
 * Loading-aware companion to {@link useExperimentalFeature}. A boolean seam is
 * enough for hiding a nav row or a card (it just stays hidden until the flag
 * resolves), but a ROUTE gate needs to distinguish "still loading" from
 * "resolved OFF" — otherwise a direct navigation to a gated path flashes the
 * 404 page before the flag arrives. `loading` is true only until the query
 * first yields data (fail-closed: `enabled` is false throughout loading/error).
 */
export function useExperimentalFeatureGate(featureId: string): ExperimentalFeatureGate {
  const { data, loading } = useQuery(ExperimentalFeaturesDocument, { fetchPolicy: 'cache-first' });
  const feature = data?.experimentalFeatures.find((f) => f.id === featureId);
  return { enabled: feature?.enabled ?? false, loading: loading && data === undefined };
}
